import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  FileText,
  Eye,
  Trash2,
  Send,
  Ban,
  CheckCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Zap,
  Download,
  Mail,
  Percent,
  DollarSign,
  Tag,
  Upload,
  Loader2,
  ScanLine,
  Paperclip,
  ChevronDown,
  Pencil,
  Save,
} from "lucide-react";
import {
  invoicesApi,
  mattersApi,
  clientsApi,
  timeEntriesApi,
  feeItemsApi,
  paymentsApi,
  disbursementPresetsApi,
  taxSettingsApi,
  PaginatedResponse,
} from "../../services/api";
import { TaxConfig, calculateTaxCents, getTaxLabel, DEFAULT_TAX_CONFIG } from "../../utils/taxCalculation";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import CreateBillingDocumentModal from "./builder/CreateBillingDocumentModal";
import QuickCreateMatterModal from "../ui/QuickCreateMatterModal";
import SampleBadge from "../ui/SampleBadge";
import ConfirmDialog from "../ui/ConfirmDialog";
import InlineNotification, { NotificationState } from "../ui/InlineNotification";
import { useUserDataContext } from "../../context/UserDataContext";

interface Invoice {
  _id: string;
  invoiceNumber: string;
  matterId: any;
  clientId: any;
  status: string;
  lineItems: any[];
  subtotalCents: number;
  discountCents: number;
  taxCents: number;
  totalCents: number;
  paidCents: number;
  balanceDueCents: number;
  issuedAt: string | null;
  dueAt: string | null;
  createdAt: string;
  notesToClient: string | null;
  sentAt?: string | null;
  sentTo?: string | null;
}

interface Props {
  onDataChange?: () => void;
}

/** YYYY-MM-DD in the user's local time zone (toISOString would give the UTC date). */
const toLocalDateString = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatCurrency = (cents: number, currency = "CAD") =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency }).format(cents / 100);

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600",
  pending_approval: "bg-yellow-100 text-yellow-700",
  issued: "bg-blue-100 text-blue-700",
  paid: "bg-emerald-100 text-emerald-700",
  partial: "bg-amber-100 text-amber-700",
  void: "bg-orange-100 text-orange-600",
  overdue: "bg-red-100 text-red-700",
};

const isOverdue = (inv: Invoice) =>
  inv.dueAt &&
  new Date(inv.dueAt) < new Date() &&
  ["issued", "partial"].includes(inv.status);

const getDisplayStatus = (inv: Invoice) =>
  isOverdue(inv) ? "overdue" : inv.status;

const formatStatus = (s: string) =>
  s
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const InvoicesTab: React.FC<Props> = ({ onDataChange }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [matters, setMatters] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);

  // Filters
  const [filterMatter, setFilterMatter] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createMatterId, setCreateMatterId] = useState("");
  const [createClientId, setCreateClientId] = useState("");
  const [createDueAt, setCreateDueAt] = useState(toLocalDateString(new Date()));
  const [createNotes, setCreateNotes] = useState("");
  const defaultLineItem = {
    kind: "fee" as const,
    description: "",
    quantity: 1,
    unitPriceDollars: 0,
    unitPriceCents: 0,
    amountCents: 0,
    taxable: true,
    taxAmountCents: 0,
    showDiscount: false,
    discountType: "percent" as "percent" | "fixed",
    discountValue: 0,
    discount: null as any,
  };
  const [createLineItems, setCreateLineItems] = useState<any[]>([{ ...defaultLineItem }]);

  // Detail view
  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [sending, setSending] = useState(false);

  // Draft inline editing
  const [editingDraft, setEditingDraft] = useState(false);
  const [draftLineItems, setDraftLineItems] = useState<any[]>([]);
  const [draftDueAt, setDraftDueAt] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);

  // Generate from time
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showCreateDocModal, setShowCreateDocModal] = useState(false);
  const [generateMatter, setGenerateMatter] = useState("");
  const [generating, setGenerating] = useState(false);

  // Quick-create matter (inline from invoice modal)
  const [showQuickMatter, setShowQuickMatter] = useState(false);
  const [quickMatterContext, setQuickMatterContext] = useState<"create" | "generate" | "scan">("create");

  // Notification & confirmation
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    invoiceId: string;
    action?: "delete" | "void";
    invoiceNumber?: string;
  }>({ open: false, invoiceId: "" });

  // Record payment against a specific invoice
  const { canManageBilling } = useUserDataContext();
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    source: "cheque",
    reference: "",
    notes: "",
  });

  // Actions dropdown
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsMenuRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target as Node)) {
        setShowActionsMenu(false);
      }
    };
    if (showActionsMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsMenu]);

  // Scan invoice
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<any>(null);
  const [scanStep, setScanStep] = useState<"upload" | "review">("upload");
  const scanFileRef = React.useRef<HTMLInputElement>(null);
  const [scanDragging, setScanDragging] = useState(false);

  // Receipt attachment (for payment recording)
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const receiptFileRef = React.useRef<HTMLInputElement>(null);

  // Disbursement presets
  const [disbursementPresets, setDisbursementPresets] = useState<any[]>([]);
  const [showSavePresetFor, setShowSavePresetFor] = useState<number | null>(null); // line item index
  const [savePresetName, setSavePresetName] = useState("");
  const [showSavePresetForDraft, setShowSavePresetForDraft] = useState<number | null>(null);
  const [savePresetNameDraft, setSavePresetNameDraft] = useState("");

  // Tax config
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);

  useEffect(() => {
    disbursementPresetsApi.list().then(res => setDisbursementPresets(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    taxSettingsApi.get().then((res) => {
      const d = res.data?.data || res.data;
      if (d?.taxMode) {
        setTaxConfig({ taxMode: d.taxMode, hstRate: d.hstRate ?? 13, gstRate: d.gstRate ?? 5, pstRate: d.pstRate ?? 0 });
      }
    }).catch(() => {});
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (filterMatter) params.matterId = filterMatter;
      if (filterClient) params.clientId = filterClient;
      if (filterStatus) params.status = filterStatus;

      const res = await invoicesApi.list(params);
      const data = res.data as PaginatedResponse<Invoice>;
      setInvoices(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterMatter, filterClient, filterStatus]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [mRes, cRes] = await Promise.all([
          mattersApi.list({ limit: 200, status: "open" }),
          clientsApi.list({ limit: 200 }),
        ]);
        setMatters(mRes.data?.data || []);
        setClients(cRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load form data:", err);
      }
    };
    loadData();
  }, []);

  const enterDraftEditMode = (inv: Invoice) => {
    setEditingDraft(true);
    setDraftLineItems(
      inv.lineItems.map((li: any) => ({
        kind: li.kind || "fee",
        description: li.description || "",
        quantity: li.quantity || 1,
        unitPriceDollars: (li.unitPriceCents || 0) / 100,
        unitPriceCents: li.unitPriceCents || 0,
        amountCents: li.amountCents || 0,
        taxable: li.taxable ?? true,
        taxAmountCents: li.taxAmountCents || 0,
        showDiscount: !!(li.discount?.type),
        discountType: (li.discount?.type || "percent") as "percent" | "fixed",
        discountValue: li.discount?.type === "percent" ? (li.discount.percent || 0) : ((li.discount?.amountCents || 0) / 100),
        discount: li.discount || null,
      }))
    );
    setDraftDueAt(inv.dueAt ? new Date(inv.dueAt).toISOString().slice(0, 10) : "");
    setDraftNotes(inv.notesToClient || "");
  };

  const viewInvoice = async (id: string) => {
    try {
      const res = await invoicesApi.get(id);
      const inv = res.data;
      setDetailInvoice(inv);
      setShowDetail(true);
      // Auto-enter edit mode for drafts
      if (inv.status === "draft") {
        enterDraftEditMode(inv);
      } else {
        setEditingDraft(false);
      }
    } catch (err) {
      console.error("Failed to fetch invoice:", err);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await invoicesApi.changeStatus(id, newStatus);
      fetchInvoices();
      onDataChange?.();
      if (showDetail) {
        const res = await invoicesApi.get(id);
        setDetailInvoice(res.data);
      }
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to change status" });
    }
  };

  const handleDeleteRequest = (id: string) => {
    setConfirmState({ open: true, invoiceId: id, action: "delete" });
  };

  const handleVoidRequest = (inv: { _id: string; invoiceNumber?: string }) => {
    setConfirmState({ open: true, invoiceId: inv._id, action: "void", invoiceNumber: inv.invoiceNumber });
  };

  const handleConfirmed = async () => {
    if (confirmState.action === "void") {
      const id = confirmState.invoiceId;
      setConfirmState({ open: false, invoiceId: "" });
      await handleStatusChange(id, "void");
      return;
    }
    await handleDeleteConfirmed();
  };

  const openRecordPayment = (inv: Invoice) => {
    setPaymentInvoice(inv);
    setPaymentError(null);
    setPaymentForm({
      amount: ((inv.balanceDueCents || 0) / 100).toFixed(2),
      source: "cheque",
      reference: "",
      notes: "",
    });
  };

  const handleRecordPayment = async () => {
    if (!paymentInvoice) return;
    const amountCents = Math.round(parseFloat(paymentForm.amount || "0") * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setPaymentError("Enter an amount greater than zero.");
      return;
    }
    if (amountCents > (paymentInvoice.balanceDueCents || 0)) {
      setPaymentError(`Amount can't be more than the balance due of ${formatCurrency(paymentInvoice.balanceDueCents)}.`);
      return;
    }
    setRecordingPayment(true);
    setPaymentError(null);
    try {
      await paymentsApi.create({
        invoiceId: paymentInvoice._id,
        source: paymentForm.source,
        amountCents,
        methodDetails: {
          chequeNumber: paymentForm.source === "cheque" ? paymentForm.reference || null : null,
          eftReference: paymentForm.source === "eft" ? paymentForm.reference || null : null,
          notes: paymentForm.notes || null,
        },
      });
      const id = paymentInvoice._id;
      setPaymentInvoice(null);
      setNotification({
        type: "success",
        message: `Payment of ${formatCurrency(amountCents)} recorded on ${paymentInvoice.invoiceNumber}`,
      });
      fetchInvoices();
      onDataChange?.();
      if (showDetail && detailInvoice?._id === id) {
        const res = await invoicesApi.get(id);
        setDetailInvoice(res.data);
      }
    } catch (err: any) {
      setPaymentError(err.response?.data?.message || err.response?.data?.error || "Couldn't record the payment.");
    } finally {
      setRecordingPayment(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    const id = confirmState.invoiceId;
    setConfirmState({ open: false, invoiceId: "" });
    try {
      await invoicesApi.remove(id);
      fetchInvoices();
      onDataChange?.();
      setNotification({ type: "success", message: "Invoice deleted" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to delete invoice" });
    }
  };

  const handleSendInvoice = async (id: string) => {
    setSending(true);
    try {
      const sendRes = await invoicesApi.send(id);
      if (sendRes.data?.warning) {
        setNotification({ type: "warning", message: sendRes.data.warning });
      } else {
        setNotification({ type: "success", message: "Invoice sent successfully" });
      }
      fetchInvoices();
      onDataChange?.();
      if (showDetail) {
        const res = await invoicesApi.get(id);
        setDetailInvoice(res.data);
      }
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to send invoice" });
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!detailInvoice) return;
    setSavingDraft(true);
    try {
      const lineItems = draftLineItems
        .filter((li) => li.description.trim())
        .map((li) => ({
          kind: li.kind,
          description: li.description,
          quantity: li.quantity,
          unitPriceCents: li.unitPriceCents,
          amountCents: li.amountCents,
          taxable: li.taxable,
          taxAmountCents: li.taxAmountCents,
          ...(li.discount ? { discount: li.discount } : {}),
        }));
      await invoicesApi.update(detailInvoice._id, {
        lineItems,
        dueAt: draftDueAt || undefined,
        notesToClient: draftNotes || null,
      });
      // Refresh
      const res = await invoicesApi.get(detailInvoice._id);
      setDetailInvoice(res.data);
      enterDraftEditMode(res.data);
      fetchInvoices();
      onDataChange?.();
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to save draft" });
    } finally {
      setSavingDraft(false);
    }
  };

  const addDraftLineItem = () => {
    setDraftLineItems([
      ...draftLineItems,
      {
        kind: "fee", description: "", quantity: 1,
        unitPriceDollars: 0, unitPriceCents: 0, amountCents: 0,
        taxable: true, taxAmountCents: 0,
        showDiscount: false, discountType: "percent" as "percent" | "fixed",
        discountValue: 0, discount: null as any,
      },
    ]);
  };

  const updateDraftLineItem = (index: number, field: string, value: any) => {
    setDraftLineItems((prev) =>
      prev.map((li, i) => {
        if (i !== index) return li;
        const updated = { ...li, [field]: value };
        if (field === "unitPriceDollars") {
          updated.unitPriceCents = Math.round((parseFloat(value) || 0) * 100);
        }
        if (field === "quantity" || field === "unitPriceDollars") {
          updated.amountCents = Math.round(updated.quantity * updated.unitPriceCents);
        }
        // Auto-calculate tax when amount changes or taxable is toggled
        if (field === "quantity" || field === "unitPriceDollars" || field === "taxable") {
          if (updated.taxable) {
            const { totalTaxCents } = calculateTaxCents(updated.amountCents, taxConfig);
            updated.taxAmountCents = totalTaxCents;
          } else {
            updated.taxAmountCents = 0;
          }
        }
        if (field === "discountType" || field === "discountValue" || field === "quantity" || field === "unitPriceDollars") {
          if (updated.showDiscount && updated.discountValue > 0) {
            if (updated.discountType === "percent") {
              const discountAmountCents = Math.round((updated.amountCents * updated.discountValue) / 100);
              updated.discount = { type: "percent", percent: updated.discountValue, amountCents: discountAmountCents };
            } else {
              const discountAmountCents = Math.round(updated.discountValue * 100);
              updated.discount = { type: "fixed", amountCents: discountAmountCents };
            }
          } else {
            updated.discount = null;
          }
        }
        return updated;
      })
    );
  };

  const toggleDraftLineDiscount = (index: number) => {
    setDraftLineItems((prev) =>
      prev.map((li, i) => {
        if (i !== index) return li;
        const show = !li.showDiscount;
        return { ...li, showDiscount: show, discountValue: show ? li.discountValue : 0, discount: show ? li.discount : null };
      })
    );
  };

  const removeDraftLineItem = (index: number) => {
    setDraftLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const getDraftLineNetAmount = (li: any) => {
    if (!li.discount) return li.amountCents;
    return li.amountCents - (li.discount.amountCents || 0);
  };

  // Draft totals for display in the drawer
  const draftSubtotal = draftLineItems.reduce((sum, li) => sum + (li.amountCents || 0), 0);
  const draftDiscount = draftLineItems.reduce((sum, li) => {
    if (!li.discount) return sum;
    return sum + (li.discount.amountCents || 0);
  }, 0);
  const draftTax = draftLineItems.reduce((sum, li) => sum + (li.taxAmountCents || 0), 0);
  const draftTotal = draftSubtotal - draftDiscount + draftTax;

  const handleGenerate = async () => {
    if (!generateMatter) return;
    setGenerating(true);
    try {
      const matter = matters.find((m: any) => m._id === generateMatter);
      const clientId =
        typeof matter?.clientId === "object" ? matter.clientId._id : matter?.clientId;
      await invoicesApi.generate({ matterId: generateMatter, clientId: clientId || "" });
      setShowGenerateModal(false);
      setGenerateMatter("");
      fetchInvoices();
      onDataChange?.();
      setNotification({ type: "success", message: "Invoice generated from time entries" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to generate invoice" });
    } finally {
      setGenerating(false);
    }
  };

  const handleClientSelect = (clientId: string) => {
    setCreateClientId(clientId);
    // Auto-select the most recent matter for this client
    const clientMattersList = matters.filter((m: any) => {
      const mClientId = typeof m.clientId === "object" ? m.clientId._id : m.clientId;
      return mClientId === clientId;
    });
    if (clientMattersList.length === 1) {
      setCreateMatterId(clientMattersList[0]._id);
    } else if (clientMattersList.length > 1) {
      // Pick the most recently updated one
      const sorted = [...clientMattersList].sort(
        (a: any, b: any) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
      );
      setCreateMatterId(sorted[0]._id);
    } else {
      setCreateMatterId("");
    }
  };

  const handleMatterSelect = (matterId: string) => {
    const matter = matters.find((m: any) => m._id === matterId);
    const clientId =
      typeof matter?.clientId === "object" ? matter.clientId._id : matter?.clientId || "";
    setCreateMatterId(matterId);
    if (!createClientId) setCreateClientId(clientId);
  };

  // Filter matters to selected client
  const clientMatters = createClientId
    ? matters.filter((m: any) => {
        const mClientId = typeof m.clientId === "object" ? m.clientId._id : m.clientId;
        return mClientId === createClientId;
      })
    : matters;

  const handleQuickMatterCreated = async (newMatter: { _id: string; title: string; matterNumber: string }) => {
    // Refresh matters list so the new one appears in dropdowns
    try {
      const mRes = await mattersApi.list({ limit: 200, status: "open" });
      setMatters(mRes.data?.data || []);
    } catch {}
    // Auto-select the newly created matter
    handleMatterSelect(newMatter._id);
    if (quickMatterContext === "generate") {
      setGenerateMatter(newMatter._id);
    }
  };

  // ── Invoice Scan Handlers ──
  const handleScanFile = async (file: File) => {
    setScanFile(file);
    setScanning(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await invoicesApi.scan(formData);
      const data = res.data;
      setScanResult(data);

      // Pre-populate the create form with extracted data
      if (data.matchedClientId) setCreateClientId(data.matchedClientId);
      if (data.extractedData) {
        const ext = data.extractedData;
        if (ext.dueDate) setCreateDueAt(ext.dueDate);
        if (ext.notes) setCreateNotes(ext.notes);

        // Pre-populate line items
        if (ext.lineItems && ext.lineItems.length > 0) {
          setCreateLineItems(
            ext.lineItems.map((li: any) => ({
              kind: "fee",
              description: li.description || "",
              quantity: li.quantity || 1,
              unitPriceDollars: li.unitPriceDollars || 0,
              unitPriceCents: Math.round((li.unitPriceDollars || 0) * 100),
              amountCents: Math.round((li.amountDollars || li.unitPriceDollars * (li.quantity || 1) || 0) * 100),
              taxable: true,
              taxAmountCents: 0,
              showDiscount: false,
              discountType: "percent" as "percent" | "fixed",
              discountValue: 0,
              discount: null as any,
            }))
          );
        }
      }
      setScanStep("review");
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to scan invoice. You can create it manually." });
      // Fallback: open the regular create modal
      setShowScanModal(false);
      setShowCreateModal(true);
    } finally {
      setScanning(false);
    }
  };

  const handleScanDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setScanDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleScanFile(file);
  };

  const resetScan = () => {
    setScanFile(null);
    setScanning(false);
    setScanResult(null);
    setScanStep("upload");
  };

  const handleScanCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createLineItems.length === 0) {
      setNotification({ type: "warning", message: "Add at least one line item" });
      return;
    }
    if (!createMatterId) {
      setNotification({ type: "warning", message: "Please select a matter" });
      return;
    }
    if (!createClientId) {
      setNotification({ type: "warning", message: "Please select a matter with a client" });
      return;
    }
    setCreating(true);
    try {
      const lineItems = createLineItems
        .filter((li) => li.description.trim())
        .map((li) => ({
          kind: li.kind,
          description: li.description || "Line item",
          quantity: li.quantity || 1,
          unitPriceCents: li.unitPriceCents || 0,
          amountCents: li.amountCents || 0,
          taxable: li.taxable,
          taxAmountCents: li.taxAmountCents || 0,
          ...(li.discount ? { discount: li.discount } : {}),
        }));

      if (lineItems.length === 0) {
        setNotification({ type: "warning", message: "Add at least one line item with a description" });
        setCreating(false);
        return;
      }

      // Default dueAt to 30 days from now if not set
      const dueAt = createDueAt || toLocalDateString(new Date(Date.now() + 30 * 86400000));

      const invoiceData: any = {
        matterId: createMatterId,
        clientId: createClientId,
        lineItems,
        dueAt,
        notesToClient: createNotes || null,
      };

      // If we have a scanned document, attach it
      if (scanResult?.documentId) {
        invoiceData.pdfDocumentId = scanResult.documentId;
      }

      await invoicesApi.create(invoiceData);
      setShowScanModal(false);
      resetScan();
      setCreateLineItems([]);
      setCreateMatterId("");
      setCreateClientId("");
      setCreateDueAt(toLocalDateString(new Date()));
      setCreateNotes("");
      fetchInvoices();
      onDataChange?.();
      setNotification({ type: "success", message: "Invoice created from scanned document" });
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to create invoice" });
    } finally {
      setCreating(false);
    }
  };

  const addLineItem = () => {
    setCreateLineItems([
      ...createLineItems,
      {
        kind: "fee",
        description: "",
        quantity: 1,
        unitPriceDollars: 0,
        unitPriceCents: 0,
        amountCents: 0,
        taxable: true,
        taxAmountCents: 0,
        showDiscount: false,
        discountType: "percent" as "percent" | "fixed",
        discountValue: 0,
        discount: null as any,
      },
    ]);
  };

  const updateLineItem = (index: number, field: string, value: any) => {
    setCreateLineItems((prev) =>
      prev.map((li, i) => {
        if (i !== index) return li;
        const updated = { ...li, [field]: value };

        // Convert dollar input to cents
        if (field === "unitPriceDollars") {
          updated.unitPriceCents = Math.round((parseFloat(value) || 0) * 100);
        }
        if (field === "quantity" || field === "unitPriceDollars") {
          updated.amountCents = Math.round(updated.quantity * updated.unitPriceCents);
        }

        // Auto-calculate tax when amount changes or taxable is toggled
        if (field === "quantity" || field === "unitPriceDollars" || field === "taxable") {
          if (updated.taxable) {
            const { totalTaxCents } = calculateTaxCents(updated.amountCents, taxConfig);
            updated.taxAmountCents = totalTaxCents;
          } else {
            updated.taxAmountCents = 0;
          }
        }

        // Recalculate discount
        if (field === "discountType" || field === "discountValue" || field === "quantity" || field === "unitPriceDollars") {
          if (updated.showDiscount && updated.discountValue > 0) {
            if (updated.discountType === "percent") {
              const discountAmountCents = Math.round((updated.amountCents * updated.discountValue) / 100);
              updated.discount = { type: "percent", percent: updated.discountValue, amountCents: discountAmountCents };
            } else {
              const discountAmountCents = Math.round(updated.discountValue * 100);
              updated.discount = { type: "fixed", amountCents: discountAmountCents };
            }
          } else {
            updated.discount = null;
          }
        }

        return updated;
      })
    );
  };

  const toggleLineDiscount = (index: number) => {
    setCreateLineItems((prev) =>
      prev.map((li, i) => {
        if (i !== index) return li;
        const show = !li.showDiscount;
        return {
          ...li,
          showDiscount: show,
          discountValue: show ? li.discountValue : 0,
          discount: show ? li.discount : null,
        };
      })
    );
  };

  const removeLineItem = (index: number) => {
    setCreateLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const getLineNetAmount = (li: any) => {
    if (!li.discount) return li.amountCents;
    return li.amountCents - (li.discount.amountCents || 0);
  };

  const handleSavePreset = async (li: any, index: number) => {
    if (!savePresetName.trim()) return;
    try {
      const res = await disbursementPresetsApi.create({
        name: savePresetName.trim(),
        defaultAmountCents: li.unitPriceCents,
        taxable: li.taxable,
        description: li.description,
      });
      setDisbursementPresets(prev => [...prev, res.data?.data || res.data]);
      setShowSavePresetFor(null);
      setSavePresetName("");
    } catch (err) {
      console.error("Failed to save preset:", err);
    }
  };

  const handleSavePresetDraft = async (li: any, index: number) => {
    if (!savePresetNameDraft.trim()) return;
    try {
      const res = await disbursementPresetsApi.create({
        name: savePresetNameDraft.trim(),
        defaultAmountCents: li.unitPriceCents,
        taxable: li.taxable,
        description: li.description,
      });
      setDisbursementPresets(prev => [...prev, res.data?.data || res.data]);
      setShowSavePresetForDraft(null);
      setSavePresetNameDraft("");
    } catch (err) {
      console.error("Failed to save preset:", err);
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createLineItems.length === 0) {
      setNotification({ type: "warning", message: "Add at least one line item" });
      return;
    }
    setCreating(true);
    try {
      // Convert line items  -  strip UI-only fields, keep backend-compatible shape
      const lineItems = createLineItems.map((li) => ({
        kind: li.kind,
        description: li.description,
        quantity: li.quantity,
        unitPriceCents: li.unitPriceCents,
        amountCents: li.amountCents,
        taxable: li.taxable,
        taxAmountCents: li.taxAmountCents,
        ...(li.discount ? { discount: li.discount } : {}),
      }));

      const res = await invoicesApi.create({
        matterId: createMatterId,
        clientId: createClientId,
        lineItems,
        dueAt: createDueAt,
        notesToClient: createNotes || null,
      });

      // Auto-issue and send the invoice immediately
      const newInvoiceId = res.data?._id || res.data?.data?._id;
      if (newInvoiceId) {
        try {
          const sendRes = await invoicesApi.send(newInvoiceId);
          if (sendRes.data?.warning) {
            setNotification({ type: "warning", message: sendRes.data.warning });
          } else {
            setNotification({ type: "success", message: "Invoice created and sent" });
          }
        } catch (sendErr: any) {
          const sendError = sendErr.response?.data?.error || sendErr.message || "Unknown error";
          setNotification({ type: "warning", message: `Invoice created but email failed to send: ${sendError}` });
        }
      }

      setShowCreateModal(false);
      setCreateLineItems([{ ...defaultLineItem }]);
      setCreateMatterId("");
      setCreateClientId("");
      setCreateDueAt(toLocalDateString(new Date()));
      setCreateNotes("");
      fetchInvoices();
      onDataChange?.();
    } catch (err: any) {
      setNotification({ type: "error", message: err.response?.data?.error || "Failed to create invoice" });
    } finally {
      setCreating(false);
    }
  };

  // Calculate totals for create modal
  const createSubtotal = createLineItems.reduce((sum, li) => sum + (li.amountCents || 0), 0);
  const createDiscount = createLineItems.reduce((sum, li) => {
    if (!li.discount) return sum;
    return sum + (li.discount.amountCents || 0);
  }, 0);
  const createTax = createLineItems.reduce((sum, li) => sum + (li.taxAmountCents || 0), 0);
  const createTotal = createSubtotal - createDiscount + createTax;

  // Get client display for selected matter
  const selectedClient = clients.find((c: any) => c._id === createClientId);

  return (
    <div className="space-y-6">
      {/* Notification */}
      <InlineNotification notification={notification} onDismiss={() => setNotification(null)} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.action === "void" ? "Void Invoice" : "Delete Draft Invoice"}
        message={
          confirmState.action === "void"
            ? `Void ${confirmState.invoiceNumber ? `invoice ${confirmState.invoiceNumber}` : "this invoice"}? The client will no longer be able to pay it and it can't be reissued.`
            : "Are you sure you want to delete this draft invoice? This action cannot be undone."
        }
        confirmLabel={confirmState.action === "void" ? "Void Invoice" : "Delete Invoice"}
        variant="danger"
        onConfirm={handleConfirmed}
        onCancel={() => setConfirmState({ open: false, invoiceId: "" })}
      />

      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative" ref={actionsMenuRef}>
          <button
            onClick={() => setShowActionsMenu(!showActionsMenu)}
            className="flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <Plus className="h-4 w-4" />
            New Invoice
            <ChevronDown className={`h-4 w-4 transition-transform ${showActionsMenu ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {showActionsMenu && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50"
              >
                <button
                  onClick={() => { setShowCreateDocModal(true); setShowActionsMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
                >
                  <div className="p-2 rounded-lg bg-brand-light">
                    <Plus className="h-4 w-4 text-brand" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Billing Document</p>
                    <p className="text-xs text-slate-500">Full invoice builder</p>
                  </div>
                </button>
                <button
                  onClick={() => { setShowCreateModal(true); setShowActionsMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
                >
                  <div className="p-2 rounded-lg bg-blue-50">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Quick Invoice</p>
                    <p className="text-xs text-slate-500">Simple line items</p>
                  </div>
                </button>
                <button
                  onClick={() => { setShowGenerateModal(true); setShowActionsMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100"
                >
                  <div className="p-2 rounded-lg bg-amber-50">
                    <Zap className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Generate from Time</p>
                    <p className="text-xs text-slate-500">Bill unbilled time entries</p>
                  </div>
                </button>
                <button
                  onClick={() => { resetScan(); setShowScanModal(true); setShowActionsMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="p-2 rounded-lg bg-purple-50">
                    <ScanLine className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Upload Invoice</p>
                    <p className="text-xs text-slate-500">Scan PDF or image with AI</p>
                  </div>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <p className="text-sm text-slate-500">{pagination.total} invoices</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchableSelect
              options={matters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
              value={filterMatter || null}
              onChange={(val) => {
                setFilterMatter(val || "");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="All Matters"
              clearable
            />
          </div>
          <SearchableSelect
            options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
            value={filterClient || null}
            onChange={(val) => {
              setFilterClient(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Clients"
            clearable
          />
          <SearchableSelect
            options={[
              { value: "draft", label: "Draft" },
              { value: "pending_approval", label: "Pending Approval" },
              { value: "issued", label: "Issued" },
              { value: "paid", label: "Paid" },
              { value: "partial", label: "Partial" },
              { value: "void", label: "Void" },
            ]}
            value={filterStatus || null}
            onChange={(val) => {
              setFilterStatus(val || "");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            placeholder="All Statuses"
            clearable
          />
        </div>
      </div>

      {/* Invoice List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : invoices.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
        >
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
            <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
              <FileText className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">No invoices yet</h3>
          <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
            Create an invoice from scratch or generate one automatically from your tracked time entries and disbursements.
          </p>
        </motion.div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                    Client
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                    Matter
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Total
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden sm:table-cell">
                    Balance Due
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">
                    Due Date
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <motion.tr
                    key={inv._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => viewInvoice(inv._id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-medium text-slate-800">{inv.invoiceNumber}</span>
                        {(inv as any).isSample && <SampleBadge />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                      {typeof inv.clientId === "object" ? inv.clientId?.displayName : "-"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                      {typeof inv.matterId === "object" ? inv.matterId?.title : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          statusColors[getDisplayStatus(inv)] || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {formatStatus(getDisplayStatus(inv))}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-slate-800 text-right">
                      {formatCurrency(inv.totalCents)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 text-right hidden sm:table-cell">
                      {formatCurrency(inv.balanceDueCents)}
                    </td>
                    <td className={`px-6 py-4 text-sm hidden xl:table-cell ${isOverdue(inv) ? "text-red-600 font-medium" : "text-slate-500"}`}>
                      {inv.dueAt
                        ? new Date(inv.dueAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "-"}
                    </td>
                    <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => viewInvoice(inv._id)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                        {inv.status === "draft" && (
                          <>
                            <button
                              onClick={() => handleStatusChange(inv._id, "issued")}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 text-xs font-medium text-slate-500 hover:text-blue-600 transition-colors"
                            >
                              <Send className="h-3.5 w-3.5" />
                              Issue
                            </button>
                            <button
                              onClick={() => handleDeleteRequest(inv._id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-red-50 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete
                            </button>
                          </>
                        )}
                        {(inv.status === "issued" || inv.status === "partial") && (
                          <>
                            <button
                              onClick={() => handleSendInvoice(inv._id)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-brand-light text-xs font-medium text-slate-500 hover:text-brand-accent transition-colors"
                            >
                              <Mail className="h-3.5 w-3.5" />
                              {(inv as any).sentAt ? "Resend" : "Send"}
                            </button>
                            {canManageBilling && (
                              <button
                                onClick={() => openRecordPayment(inv)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-emerald-50 text-xs font-medium text-slate-500 hover:text-emerald-700 transition-colors"
                              >
                                <DollarSign className="h-3.5 w-3.5" />
                                Record Payment
                              </button>
                            )}
                            <button
                              onClick={() => handleVoidRequest(inv)}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-red-50 text-xs font-medium text-slate-500 hover:text-red-600 transition-colors"
                            >
                              <Ban className="h-3.5 w-3.5" />
                              Void
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </p>
              <div className="flex gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invoice Detail Drawer */}
      <AnimatePresence>
        {showDetail && detailInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex justify-end"
            onClick={() => setShowDetail(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-slate-800">
                        {detailInvoice.invoiceNumber}
                      </h2>
                      {(detailInvoice as any).isSample && <SampleBadge />}
                    </div>
                    <span
                      className={`mt-1 inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[detailInvoice.status] || "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {formatStatus(detailInvoice.status)}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowDetail(false)}
                    className="p-2 rounded-lg hover:bg-slate-100"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>

                {/* Sent badge */}
                {(detailInvoice as any).sentAt && (
                  <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-200">
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700">
                      Sent to {(detailInvoice as any).sentTo} on{" "}
                      {new Date((detailInvoice as any).sentAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}

                {/* Summary */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Client</p>
                    <p className="text-sm font-medium text-slate-800">
                      {typeof detailInvoice.clientId === "object"
                        ? detailInvoice.clientId?.displayName
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Matter</p>
                    <p className="text-sm font-medium text-slate-800">
                      {typeof detailInvoice.matterId === "object"
                        ? detailInvoice.matterId?.title
                        : "-"}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Due Date</p>
                    {editingDraft ? (
                      <input
                        type="date"
                        value={draftDueAt}
                        onChange={(e) => setDraftDueAt(e.target.value)}
                        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-600"
                      />
                    ) : (
                      <p className="text-sm font-medium text-slate-800">
                        {detailInvoice.dueAt
                          ? new Date(detailInvoice.dueAt).toLocaleDateString()
                          : "-"}
                      </p>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500">Issued Date</p>
                    <p className="text-sm font-medium text-slate-800">
                      {detailInvoice.issuedAt
                        ? new Date(detailInvoice.issuedAt).toLocaleDateString()
                        : "Not issued"}
                    </p>
                  </div>
                </div>

                {/* Line Items */}
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center justify-between">
                  Line Items
                  {editingDraft && (
                    <button
                      type="button"
                      onClick={addDraftLineItem}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                      style={{ color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, transparent)" }}
                    >
                      + Add Item
                    </button>
                  )}
                </h3>
                {editingDraft ? (
                  <div className="space-y-3 mb-6">
                    {draftLineItems.map((li, i) => (
                      <div key={i} className={`rounded-xl p-3 ${li.kind === "disbursement" ? "bg-amber-50 border-l-4 border-amber-400" : "bg-slate-50"}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="text"
                            placeholder="Description"
                            value={li.description}
                            onChange={(e) => updateDraftLineItem(i, "description", e.target.value)}
                            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                          />
                          <button
                            type="button"
                            onClick={() => removeDraftLineItem(i)}
                            className="p-1.5 rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-slate-500">Qty</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={li.quantity}
                              onChange={(e) => updateDraftLineItem(i, "quantity", parseFloat(e.target.value) || 0)}
                              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Rate ($)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={li.unitPriceDollars || ""}
                              onChange={(e) => updateDraftLineItem(i, "unitPriceDollars", e.target.value)}
                              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
                            />
                          </div>
                          <div className="flex items-end gap-1">
                            <div className="flex-1">
                              <label className="text-xs text-slate-500">Amount</label>
                              <p className="px-3 py-1.5 text-sm font-medium text-slate-800">
                                {formatCurrency(li.showDiscount && li.discount ? getDraftLineNetAmount(li) : li.amountCents)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleDraftLineDiscount(i)}
                              className={`p-1.5 rounded-lg transition-colors mb-0.5 ${li.showDiscount ? "bg-red-100 text-red-600" : "hover:bg-slate-200 text-slate-400"}`}
                              title="Toggle discount"
                            >
                              <Tag className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        {li.showDiscount && (
                          <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-3">
                            <span className="text-xs font-medium text-red-600">Discount:</span>
                            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                              <button type="button" onClick={() => updateDraftLineItem(i, "discountType", "percent")}
                                className={`px-2 py-1 text-xs font-medium ${li.discountType === "percent" ? "bg-red-100 text-red-700" : "bg-white text-slate-500"}`}>%</button>
                              <button type="button" onClick={() => updateDraftLineItem(i, "discountType", "fixed")}
                                className={`px-2 py-1 text-xs font-medium border-l border-slate-200 ${li.discountType === "fixed" ? "bg-red-100 text-red-700" : "bg-white text-slate-500"}`}>$</button>
                            </div>
                            <input type="number" min="0" step={li.discountType === "percent" ? "1" : "0.01"}
                              value={li.discountValue || ""}
                              onChange={(e) => updateDraftLineItem(i, "discountValue", parseFloat(e.target.value) || 0)}
                              className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                            />
                            {li.discount && li.discount.amountCents > 0 && (
                              <span className="text-xs font-medium text-red-600">-{formatCurrency(li.discount.amountCents)}</span>
                            )}
                          </div>
                        )}

                        {/* Disbursement preset row (draft) */}
                        {li.kind === "disbursement" && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {disbursementPresets.length > 0 && (
                              <div className="flex items-center gap-1 min-w-[160px]">
                                <span className="text-xs text-slate-400">Load:</span>
                                <SearchableSelect
                                  options={disbursementPresets.map((p: any) => ({ value: p._id, label: p.name }))}
                                  value={null}
                                  onChange={(val) => {
                                    const preset = disbursementPresets.find(p => p._id === val);
                                    if (preset) {
                                      updateDraftLineItem(i, "description", preset.description || preset.name);
                                      updateDraftLineItem(i, "unitPriceDollars", (preset.defaultAmountCents / 100).toFixed(2));
                                      updateDraftLineItem(i, "taxable", preset.taxable);
                                    }
                                  }}
                                  placeholder="-- preset --"
                                />
                              </div>
                            )}
                            {showSavePresetForDraft === i ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  placeholder="Preset name..."
                                  value={savePresetNameDraft}
                                  onChange={(e) => setSavePresetNameDraft(e.target.value)}
                                  className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-500 w-36"
                                  autoFocus
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSavePresetDraft(li, i)}
                                  className="text-xs px-2 py-1 rounded-lg font-medium text-white"
                                  style={{ backgroundColor: "var(--color-primary)" }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setShowSavePresetForDraft(null); setSavePresetNameDraft(""); }}
                                  className="text-xs px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-100"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => { setShowSavePresetForDraft(i); setSavePresetNameDraft(li.description || ""); }}
                                className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
                              >
                                Save as preset
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600">
                            Description
                          </th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">
                            Qty
                          </th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">
                            Rate
                          </th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600">
                            Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailInvoice.lineItems.map((li: any, i: number) => {
                          const hasDiscount = li.discount?.type && (li.discount.amountCents > 0 || li.discount.percent > 0);
                          const discountAmount = hasDiscount
                            ? li.discount.type === "percent"
                              ? Math.round((li.amountCents * li.discount.percent) / 100)
                              : li.discount.amountCents || 0
                            : 0;
                          const netAmount = li.amountCents - discountAmount;

                          return (
                            <tr key={i} className="border-b border-slate-100 last:border-b-0">
                              <td className="px-4 py-3">
                                <span className="text-slate-800">{li.description}</span>
                                {hasDiscount && (
                                  <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium">
                                    {li.discount.type === "percent"
                                      ? `-${li.discount.percent}%`
                                      : `-${formatCurrency(li.discount.amountCents)}`}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600">
                                {li.quantity?.toFixed(2)}
                              </td>
                              <td className="px-4 py-3 text-right text-slate-600">
                                {formatCurrency(li.unitPriceCents)}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {hasDiscount ? (
                                  <div>
                                    <span className="text-slate-400 line-through text-xs">
                                      {formatCurrency(li.amountCents)}
                                    </span>
                                    <br />
                                    <span className="font-medium text-slate-800">
                                      {formatCurrency(netAmount)}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="font-medium text-slate-800">
                                    {formatCurrency(li.amountCents)}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totals */}
                {editingDraft ? (
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-800">{formatCurrency(draftSubtotal)}</span>
                    </div>
                    {draftDiscount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Discount</span>
                        <span className="text-red-600">-{formatCurrency(draftDiscount)}</span>
                      </div>
                    )}
                    {draftTax > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">{getTaxLabel(taxConfig)}</span>
                        <span className="text-slate-800">{formatCurrency(draftTax)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2">
                      <span className="text-slate-800">Total</span>
                      <span className="text-slate-800">{formatCurrency(draftTotal)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-800">
                        {formatCurrency(detailInvoice.subtotalCents)}
                      </span>
                    </div>
                    {detailInvoice.discountCents > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Discount</span>
                        <span className="text-red-600">
                          -{formatCurrency(detailInvoice.discountCents)}
                        </span>
                      </div>
                    )}
                    {detailInvoice.taxCents > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">{getTaxLabel(taxConfig)}</span>
                        <span className="text-slate-800">
                          {formatCurrency(detailInvoice.taxCents)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-2">
                      <span className="text-slate-800">Total</span>
                      <span className="text-slate-800">
                        {formatCurrency(detailInvoice.totalCents)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Paid</span>
                      <span className="text-emerald-600">
                        {formatCurrency(detailInvoice.paidCents)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-bold">
                      <span className="text-slate-800">Balance Due</span>
                      <span className="text-slate-800">
                        {formatCurrency(detailInvoice.balanceDueCents)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {editingDraft ? (
                  <div className="mb-6">
                    <p className="text-xs text-slate-500 mb-1">Notes to Client</p>
                    <textarea
                      value={draftNotes}
                      onChange={(e) => setDraftNotes(e.target.value)}
                      rows={2}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                      placeholder="Any notes for the client..."
                    />
                  </div>
                ) : detailInvoice.notesToClient ? (
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <p className="text-xs text-slate-500 mb-1">Notes to Client</p>
                    <p className="text-sm text-slate-700">{detailInvoice.notesToClient}</p>
                  </div>
                ) : null}

                {/* Actions */}
                {editingDraft && (
                  <div className="mb-3">
                    <button
                      onClick={handleSaveDraft}
                      disabled={savingDraft}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    >
                      <Save className="h-4 w-4" />
                      {savingDraft ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {detailInvoice.status === "draft" && (
                    <>
                      <button
                        onClick={() => handleStatusChange(detailInvoice._id, "issued")}
                        className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition-colors"
                      >
                        <Send className="h-4 w-4" />
                        Issue Invoice
                      </button>
                      <button
                        onClick={() => handleSendInvoice(detailInvoice._id)}
                        disabled={sending}
                        className="flex items-center gap-2 px-4 py-2.5 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        <Mail className="h-4 w-4" />
                        {sending ? "Sending..." : "Issue & Send to Client"}
                      </button>
                    </>
                  )}
                  {(detailInvoice.status === "issued" ||
                    detailInvoice.status === "partial") && (
                    <>
                      <button
                        onClick={() => handleSendInvoice(detailInvoice._id)}
                        disabled={sending}
                        className="flex items-center gap-2 px-4 py-2.5 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-50"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        <Mail className="h-4 w-4" />
                        {sending
                          ? "Sending..."
                          : (detailInvoice as any).sentAt
                            ? "Resend to Client"
                            : "Send to Client"}
                      </button>
                      {canManageBilling && (
                        <button
                          onClick={() => openRecordPayment(detailInvoice)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold rounded-xl text-sm transition-colors"
                        >
                          <DollarSign className="h-4 w-4" />
                          Record Payment
                        </button>
                      )}
                      <button
                        onClick={() => handleVoidRequest(detailInvoice)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl text-sm transition-colors"
                      >
                        <Ban className="h-4 w-4" />
                        Void
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Record Payment Modal */}
      <PremiumModal
        open={!!paymentInvoice}
        onClose={() => setPaymentInvoice(null)}
        title={paymentInvoice ? `Record Payment: ${paymentInvoice.invoiceNumber}` : "Record Payment"}
        icon={DollarSign}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setPaymentInvoice(null)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRecordPayment}
              disabled={recordingPayment || !(parseFloat(paymentForm.amount) > 0)}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {recordingPayment ? "Recording..." : "Record Payment"}
            </button>
          </div>
        }
      >
        {paymentInvoice && (
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm flex justify-between">
              <span className="text-slate-500">Balance due</span>
              <span className="font-semibold text-slate-800">{formatCurrency(paymentInvoice.balanceDueCents)}</span>
            </div>
            {paymentError && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {paymentError}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount ($) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={paymentForm.amount}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, amount: e.target.value.replace(/[^0-9.]/g, "") })
                }
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Source *</label>
              <SearchableSelect
                options={[
                  { value: "cheque", label: "Cheque" },
                  { value: "eft", label: "EFT" },
                  { value: "cash", label: "Cash" },
                  { value: "stripe", label: "Stripe" },
                  { value: "trust_transfer", label: "Trust Transfer" },
                ]}
                value={paymentForm.source}
                onChange={(val) => setPaymentForm({ ...paymentForm, source: val || "cheque", reference: "" })}
                placeholder="Select source"
              />
            </div>
            {(paymentForm.source === "cheque" || paymentForm.source === "eft") && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {paymentForm.source === "cheque" ? "Cheque Number" : "EFT Reference"}
                </label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>
        )}
      </PremiumModal>

      {/* Quick Invoice Modal */}
      <PremiumModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Quick Invoice"
        icon={FileText}
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateInvoice as any}
              disabled={creating || !createMatterId || createLineItems.length === 0}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {creating ? "Sending..." : "Create & Send"}
            </button>
          </div>
        }
      >
        <form onSubmit={handleCreateInvoice} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client *
              </label>
              <SearchableSelect
                options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
                value={createClientId || null}
                onChange={(val) => handleClientSelect(val || "")}
                placeholder="Select a client"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Matter *
              </label>
              <SearchableSelect
                options={clientMatters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
                value={createMatterId || null}
                onChange={(val) => handleMatterSelect(val || "")}
                placeholder={createClientId ? "Select a matter" : "Select client first"}
                disabled={!createClientId}
                required
                addNew={{
                  label: "New Matter",
                  onAdd: () => { setQuickMatterContext("create"); setShowQuickMatter(true); },
                }}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Due Date *
            </label>
            <input
              type="date"
              required
              value={createDueAt}
              onChange={(e) => setCreateDueAt(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Line Items *</label>
              <button
                type="button"
                onClick={addLineItem}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                style={{ color: "var(--color-primary)", backgroundColor: "color-mix(in srgb, var(--color-primary) 10%, transparent)" }}
              >
                + Add Item
              </button>
            </div>
            {createLineItems.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">
                No line items yet. Click "Add Item" to start.
              </p>
            ) : (
              <div className="space-y-3">
                {createLineItems.map((li, i) => (
                  <div key={i} className={`rounded-xl p-4 ${li.kind === "disbursement" ? "bg-amber-50 border-l-4 border-amber-400" : "bg-slate-50"}`}>
                    <div className="grid grid-cols-12 gap-3">
                      {/* Description + Remove */}
                      <div className="col-span-12 flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Description *"
                          required
                          value={li.description}
                          onChange={(e) =>
                            updateLineItem(i, "description", e.target.value)
                          }
                          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                        />
                        <button
                          type="button"
                          onClick={() => removeLineItem(i)}
                          className="p-1.5 rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {/* Kind */}
                      <div className="col-span-3">
                        <SearchableSelect
                          options={[
                            { value: "fee", label: "Fee" },
                            { value: "disbursement", label: "Disbursement" },
                            { value: "time", label: "Time" },
                            { value: "credit", label: "Credit" },
                          ]}
                          value={li.kind}
                          onChange={(val) => updateLineItem(i, "kind", val || "fee")}
                          placeholder="Kind"
                        />
                      </div>
                      {/* Qty */}
                      <div className="col-span-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Qty"
                          value={li.quantity}
                          onChange={(e) =>
                            updateLineItem(i, "quantity", parseFloat(e.target.value) || 0)
                          }
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                      </div>
                      {/* Unit Price in dollars */}
                      <div className="col-span-3">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={li.unitPriceDollars || ""}
                            onChange={(e) =>
                              updateLineItem(i, "unitPriceDollars", e.target.value)
                            }
                            className="w-full rounded-lg border border-slate-200 pl-7 pr-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                      {/* Amount + Taxable + Discount toggle */}
                      <div className="col-span-4 flex items-center gap-2">
                        <div className="flex-1 text-right">
                          {li.showDiscount && li.discount ? (
                            <div>
                              <span className="text-xs text-slate-400 line-through">
                                {formatCurrency(li.amountCents)}
                              </span>
                              <br />
                              <span className="text-sm font-bold text-slate-800">
                                {formatCurrency(getLineNetAmount(li))}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm font-medium text-slate-700">
                              = {formatCurrency(li.amountCents)}
                            </span>
                          )}
                        </div>
                        <label className="flex items-center gap-1 cursor-pointer flex-shrink-0" title="Taxable">
                          <input
                            type="checkbox"
                            checked={li.taxable}
                            onChange={(e) => updateLineItem(i, "taxable", e.target.checked)}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 h-3.5 w-3.5"
                          />
                          <span className="text-[10px] text-slate-500">Tax</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => toggleLineDiscount(i)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            li.showDiscount
                              ? "bg-red-100 text-red-600"
                              : "hover:bg-slate-200 text-slate-400"
                          }`}
                          title="Toggle discount"
                        >
                          <Tag className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Discount row */}
                    {li.showDiscount && (
                      <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3">
                        <span className="text-xs font-medium text-red-600 flex-shrink-0">Discount:</span>
                        {/* Type toggle */}
                        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => updateLineItem(i, "discountType", "percent")}
                            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                              li.discountType === "percent"
                                ? "bg-red-100 text-red-700"
                                : "bg-white text-slate-500 hover:bg-slate-50"
                            }`}
                          >
                            %
                          </button>
                          <button
                            type="button"
                            onClick={() => updateLineItem(i, "discountType", "fixed")}
                            className={`px-2.5 py-1 text-xs font-medium border-l border-slate-200 transition-colors ${
                              li.discountType === "fixed"
                                ? "bg-red-100 text-red-700"
                                : "bg-white text-slate-500 hover:bg-slate-50"
                            }`}
                          >
                            $
                          </button>
                        </div>
                        {/* Value input */}
                        <div className="relative w-24">
                          {li.discountType === "fixed" && (
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                          )}
                          <input
                            type="number"
                            min="0"
                            step={li.discountType === "percent" ? "1" : "0.01"}
                            placeholder={li.discountType === "percent" ? "10" : "0.00"}
                            value={li.discountValue || ""}
                            onChange={(e) =>
                              updateLineItem(i, "discountValue", parseFloat(e.target.value) || 0)
                            }
                            className={`w-full rounded-lg border border-slate-200 pr-2 py-1.5 text-xs ${
                              li.discountType === "fixed" ? "pl-6" : "pl-2.5"
                            }`}
                          />
                          {li.discountType === "percent" && (
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                          )}
                        </div>
                        {/* Discount amount display */}
                        {li.discount && li.discount.amountCents > 0 && (
                          <span className="text-xs font-medium text-red-600">
                            -{formatCurrency(li.discount.amountCents)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Disbursement preset row */}
                    {li.kind === "disbursement" && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {disbursementPresets.length > 0 && (
                          <div className="flex items-center gap-1 min-w-[160px]">
                            <span className="text-xs text-slate-400">Load:</span>
                            <SearchableSelect
                              options={disbursementPresets.map((p: any) => ({ value: p._id, label: p.name }))}
                              value={null}
                              onChange={(val) => {
                                const preset = disbursementPresets.find(p => p._id === val);
                                if (preset) {
                                  updateLineItem(i, "description", preset.description || preset.name);
                                  updateLineItem(i, "unitPriceDollars", (preset.defaultAmountCents / 100).toFixed(2));
                                  updateLineItem(i, "taxable", preset.taxable);
                                }
                              }}
                              placeholder="-- preset --"
                            />
                          </div>
                        )}
                        {showSavePresetFor === i ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              placeholder="Preset name..."
                              value={savePresetName}
                              onChange={(e) => setSavePresetName(e.target.value)}
                              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-500 w-36"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => handleSavePreset(li, i)}
                              className="text-xs px-2 py-1 rounded-lg font-medium text-white"
                              style={{ backgroundColor: "var(--color-primary)" }}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => { setShowSavePresetFor(null); setSavePresetName(""); }}
                              className="text-xs px-2 py-1 rounded-lg text-slate-500 hover:bg-slate-100"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => { setShowSavePresetFor(i); setSavePresetName(li.description || ""); }}
                            className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2"
                          >
                            Save as preset
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes to Client
            </label>
            <textarea
              value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              rows={2}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Any notes for the client..."
            />
          </div>

          {/* Totals */}
          {createLineItems.length > 0 && (
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="text-slate-800">{formatCurrency(createSubtotal)}</span>
                </div>
                {createDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600">Discount</span>
                    <span className="text-red-600">-{formatCurrency(createDiscount)}</span>
                  </div>
                )}
                {createTax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">{getTaxLabel(taxConfig)}</span>
                    <span className="text-slate-800">{formatCurrency(createTax)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2">
                  <span className="text-slate-800">Total</span>
                  <span className="text-slate-800">{formatCurrency(createTotal)}</span>
                </div>
              </div>
            </div>
          )}
        </form>
      </PremiumModal>

      {/* Generate from Time Modal */}
      <PremiumModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        title="Generate Invoice from Time"
        icon={Zap}
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowGenerateModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || !generateMatter}
              className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              {generating ? "Generating..." : "Generate Invoice"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 mb-4">
          This will create a draft invoice from all unbilled time entries for the selected
          matter.
        </p>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
          <SearchableSelect
            options={matters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
            value={generateMatter || null}
            onChange={(val) => setGenerateMatter(val || "")}
            placeholder="Select a matter"
            required
            addNew={{
              label: "New Matter",
              onAdd: () => { setQuickMatterContext("generate"); setShowQuickMatter(true); },
            }}
          />
        </div>
      </PremiumModal>

      <CreateBillingDocumentModal
        open={showCreateDocModal}
        onClose={() => setShowCreateDocModal(false)}
      />

      {/* ── Upload/Scan Invoice Modal ── */}
      <PremiumModal
        open={showScanModal}
        onClose={() => { setShowScanModal(false); resetScan(); }}
        title={scanStep === "upload" ? "Upload Existing Invoice" : "Review Scanned Invoice"}
        subtitle={scanStep === "upload" ? "Upload a PDF or image  -  we'll extract the data" : "Review and edit the extracted data, then create"}
        icon={ScanLine}
        size="lg"
        footer={
          scanStep === "review" ? (
            <div className="flex justify-between items-center w-full">
              <button
                onClick={() => { resetScan(); }}
                className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Back
              </button>
              <button
                onClick={handleScanCreateInvoice}
                disabled={creating || createLineItems.length === 0 || !createMatterId}
                className="px-5 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                {creating ? "Creating..." : "Create Invoice"}
              </button>
            </div>
          ) : undefined
        }
      >
        {/* Step 1: Upload */}
        {scanStep === "upload" && (
          <div>
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
                scanDragging
                  ? "border-brand-accent bg-brand-light"
                  : "border-slate-300 hover:border-slate-400 bg-slate-50"
              }`}
              onDragOver={(e) => { e.preventDefault(); setScanDragging(true); }}
              onDragLeave={() => setScanDragging(false)}
              onDrop={handleScanDrop}
              onClick={() => scanFileRef.current?.click()}
            >
              {scanning ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-3" />
                  <p className="text-sm font-medium text-slate-700">Scanning invoice...</p>
                  <p className="text-xs text-slate-400 mt-1">AI is extracting data from your document</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="h-10 w-10 text-slate-400 mb-3" />
                  <p className="text-sm font-medium text-slate-700">
                    Drop your invoice here or click to browse
                  </p>
                  <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG up to 50 MB</p>
                </div>
              )}
            </div>
            <input
              ref={scanFileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleScanFile(f);
              }}
            />
            {scanFile && !scanning && (
              <p className="mt-3 text-sm text-slate-600 flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                {scanFile.name} ({(scanFile.size / 1024 / 1024).toFixed(1)} MB)
              </p>
            )}
          </div>
        )}

        {/* Step 2: Review extracted data */}
        {scanStep === "review" && (
          <form onSubmit={handleScanCreateInvoice} className="space-y-4">
            {scanResult?.extractedData?.clientName && !scanResult?.matchedClientId && (
              <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-700">
                AI detected client: <strong>{scanResult.extractedData.clientName}</strong>  -  no match found. Please select manually.
              </div>
            )}

            {scanResult?.documentId && (
              <div className="p-3 bg-emerald-50 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Original document saved. It will be attached to this invoice.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Client *</label>
                <SearchableSelect
                  options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
                  value={createClientId || null}
                  onChange={(val) => handleClientSelect(val || "")}
                  placeholder="Select client"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Matter *</label>
                <SearchableSelect
                  options={clientMatters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
                  value={createMatterId || null}
                  onChange={(val) => handleMatterSelect(val || "")}
                  placeholder={createClientId ? "Select matter" : "Select client first"}
                  disabled={!createClientId}
                  required
                  addNew={{
                    label: "New Matter",
                    onAdd: () => { setQuickMatterContext("scan"); setShowQuickMatter(true); },
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Invoice Date</label>
                <input
                  type="date"
                  value={scanResult?.extractedData?.date || ""}
                  readOnly
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm bg-slate-50"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1 font-medium">Due Date</label>
                <input
                  type="date"
                  value={createDueAt}
                  onChange={(e) => setCreateDueAt(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-slate-500 font-medium">Line Items</label>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="text-xs font-medium px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
                  style={{ color: "var(--color-primary)" }}
                >
                  + Add Item
                </button>
              </div>
              <div className="space-y-2">
                {createLineItems.map((li, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="text"
                        value={li.description}
                        onChange={(e) => updateLineItem(idx, "description", e.target.value)}
                        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-600"
                        placeholder="Description"
                      />
                      <input
                        type="number"
                        value={li.quantity}
                        onChange={(e) => updateLineItem(idx, "quantity", parseFloat(e.target.value) || 0)}
                        className="w-16 rounded-lg border border-slate-200 px-3 py-2 text-sm text-center focus:outline-none focus:border-emerald-600"
                        min="0"
                        step="0.5"
                      />
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-slate-400 text-sm">$</span>
                        <input
                          type="number"
                          value={li.unitPriceDollars || ""}
                          onChange={(e) => updateLineItem(idx, "unitPriceDollars", e.target.value)}
                          className="w-28 rounded-lg border border-slate-200 pl-7 pr-3 py-2 text-sm text-right focus:outline-none focus:border-emerald-600"
                          placeholder="0.00"
                          step="0.01"
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700 py-2 w-24 text-right">
                        {formatCurrency(li.amountCents)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeLineItem(idx)}
                        className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            {createLineItems.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex justify-between text-base font-bold">
                  <span className="text-slate-800">Total</span>
                  <span className="text-slate-800">{formatCurrency(createTotal)}</span>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs text-slate-500 mb-1 font-medium">Notes</label>
              <textarea
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                rows={2}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                placeholder="Any notes..."
              />
            </div>
          </form>
        )}
      </PremiumModal>

      {/* Quick-create matter modal (from invoice/generate/scan) */}
      <QuickCreateMatterModal
        open={showQuickMatter}
        onClose={() => setShowQuickMatter(false)}
        onCreated={handleQuickMatterCreated}
      />
    </div>
  );
};

export default InvoicesTab;
